import type {
  BehaviorCategory,
  BehaviorIncident,
  BehaviorPattern,
  ClassDisciplinarySummary,
  DisciplinaryAction,
  DisciplinaryReport,
  InterventionAction,
  InterventionPlan,
} from "../../services/disciplineService";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Conduct cases, learner support plans and the patterns behind them, and the
 * rules the screens explain. Choices mirror apps/discipline/models.py; the
 * workflow rules mirror apps/discipline/views.py.
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const when = (value?: string | null) => (value ? new Date(value).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");
export const labelFrom = (pairs: [string, string][], value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? humanise(value);

export const SEVERITIES: [string, string][] = [["1", "Minor"], ["2", "Low"], ["3", "Medium"], ["4", "High"], ["5", "Critical"]];
export const severityLabel = (level: number) => `${level} · ${labelFrom(SEVERITIES, String(level))}`;
export const SANCTION_TYPES: [string, string][] = [
  ["warning", "Verbal warning"],
  ["written_warning", "Written warning"],
  ["detention", "Detention"],
  ["suspension", "Suspension"],
  ["expulsion", "Expulsion"],
  ["parent_conference", "Parent conference"],
  ["counseling", "Counselling"],
  ["community_service", "Community service"],
  ["probation", "Probation"],
  ["other", "Other"],
];
export const PLAN_TYPES: [string, string][] = [["preventive", "Preventive"], ["corrective", "Corrective"], ["supportive", "Supportive"], ["comprehensive", "Comprehensive"]];
export const SESSION_TYPES: [string, string][] = [
  ["counseling", "Counselling session"],
  ["mentoring", "Mentoring"],
  ["academic_support", "Academic support"],
  ["behavior_contract", "Behaviour contract"],
  ["parent_meeting", "Parent meeting"],
  ["peer_mediation", "Peer mediation"],
  ["community_service", "Community service"],
  ["skill_building", "Skill building"],
  ["monitoring", "Closer monitoring"],
  ["other", "Other"],
];
export const REPORT_TYPES: [string, string][] = [
  ["comprehensive", "Comprehensive"],
  ["student_summary", "Learner summary"],
  ["class_summary", "Class summary"],
  ["incident_analysis", "Incident analysis"],
  ["intervention_effectiveness", "Support plan effectiveness"],
  ["trend_analysis", "Trend analysis"],
];
export const RATINGS: [string, string][] = [["5", "Very good"], ["4", "Good"], ["3", "Some progress"], ["2", "Little progress"], ["1", "No progress"]];

// --- Incidents -------------------------------------------------------------------------------

export function incidentStatus(row: Pick<BehaviorIncident, "status">) {
  switch (row.status) {
    case "investigating":
      return { label: "Investigating", dot: "bg-sky-500" };
    case "escalated":
      return { label: "Escalated", dot: "bg-red-500" };
    case "resolved":
      return { label: "Resolved", dot: "bg-emerald-500" };
    case "closed":
      return { label: "Closed", dot: "bg-gray-400" };
    default:
      return { label: "Reported", dot: "bg-indigo-500" };
  }
}

export const isOpenCase = (row: Pick<BehaviorIncident, "status">) => ["reported", "investigating", "escalated"].includes(row.status);
export const awaitsApproval = (row: Pick<BehaviorIncident, "requires_approval" | "approval_status">) => row.requires_approval && row.approval_status === "pending";

export function approvalLabel(row: Pick<BehaviorIncident, "requires_approval" | "approval_status">) {
  if (!row.requires_approval) return "Not needed";
  return { pending: "Waiting for leadership", approved: "Approved", rejected: "Rejected", not_required: "Not needed" }[row.approval_status];
}

/** What still stands between an open case and its resolution; mirrors resolve_incident. */
export function resolveBlockers(row: Pick<BehaviorIncident, "status" | "requires_approval" | "approval_status" | "action_needs_parent_notice" | "parent_notified">) {
  const blockers: string[] = [];
  if (row.status === "reported") blockers.push("The committee must open an investigation first.");
  if (row.requires_approval && row.approval_status !== "approved") {
    blockers.push(row.approval_status === "rejected" ? "Leadership rejected the proposed action; it needs deciding again." : "Leadership must approve the proposed action.");
  }
  if (row.action_needs_parent_notice && !row.parent_notified) blockers.push("The parent must be told first.");
  return blockers;
}

/** The one thing to do next on a case, for the table. */
export function nextStep(row: Parameters<typeof resolveBlockers>[0]) {
  if (row.status === "closed") return "—";
  if (row.status === "resolved") return "Close the case";
  if (row.status === "reported") return "Investigate";
  if (row.requires_approval && row.approval_status === "pending") return "Leadership approval";
  if (row.requires_approval && row.approval_status === "rejected") return "Decide again";
  if (row.action_needs_parent_notice && !row.parent_notified) return "Tell the parent";
  return "Resolve";
}

export const incidentConfig = (me: number | undefined): RegisterConfig<BehaviorIncident> => ({
  search: (row) => [row.student_name, row.student_id, row.student_class, row.title, row.category_name, row.action_taken_name, row.location, row.reported_by_name].join(" "),
  views: [
    { key: "open", label: "Open", match: isOpenCase },
    { key: "approval", label: "Waiting for approval", match: awaitsApproval },
    { key: "mine", label: "Reported by me", match: (row) => row.reported_by === me },
    { key: "resolved", label: "Resolved", match: (row) => row.status === "resolved" },
    { key: "closed", label: "Closed", match: (row) => row.status === "closed" },
    { key: "all", label: "All" },
  ],
  date: { label: "Incident date", value: (row) => row.incident_date },
  category: { label: "Behaviour", value: (row) => row.category_name },
  sorts: [
    { key: "-when", label: "Newest first", compare: reversed(byText<BehaviorIncident>((row) => row.incident_date)) },
    { key: "when", label: "Oldest first", compare: byText<BehaviorIncident>((row) => row.incident_date) },
    { key: "-severity", label: "Most serious", compare: (a, b) => b.severity_level - a.severity_level || b.incident_date.localeCompare(a.incident_date) },
    { key: "learner", label: "Learner (A–Z)", compare: byText<BehaviorIncident>((row) => row.student_name) },
  ],
});

// --- Support plans ---------------------------------------------------------------------------

export const isOpenSession = (row: Pick<InterventionAction, "status">) => ["scheduled", "in_progress", "rescheduled"].includes(row.status);

export function planStatus(row: Pick<InterventionPlan, "status" | "next_review_date" | "parent_involvement" | "parent_consent">, today: string) {
  switch (row.status) {
    case "draft":
      return row.parent_involvement && !row.parent_consent ? { label: "Draft, consent needed", dot: "bg-gray-400" } : { label: "Draft, ready to start", dot: "bg-indigo-500" };
    case "active":
      return row.next_review_date && row.next_review_date <= today ? { label: "Review due", dot: "bg-amber-500" } : { label: "Active", dot: "bg-sky-500" };
    case "suspended":
      return { label: "Paused", dot: "bg-amber-500" };
    case "completed":
      return { label: "Completed", dot: "bg-emerald-500" };
    default:
      return { label: "Cancelled", dot: "bg-gray-400" };
  }
}

export const reviewDue = (row: Pick<InterventionPlan, "status" | "next_review_date">, today: string) => row.status === "active" && Boolean(row.next_review_date && row.next_review_date <= today);

export const planConfig = (today: string): RegisterConfig<InterventionPlan> => ({
  search: (row) => [row.student_name, row.student_id, row.student_class, row.title, row.primary_goal, ...row.responsible_staff_names].join(" "),
  views: [
    { key: "active", label: "Active", match: (row) => row.status === "active" },
    { key: "review", label: "Review due", match: (row) => reviewDue(row, today) },
    { key: "draft", label: "Drafts", match: (row) => row.status === "draft" },
    { key: "paused", label: "Paused", match: (row) => row.status === "suspended" },
    { key: "finished", label: "Finished", match: (row) => row.status === "completed" || row.status === "cancelled" },
    { key: "all", label: "All" },
  ],
  date: { label: "Starts", value: (row) => row.start_date },
  category: { label: "Plan type", value: (row) => labelFrom(PLAN_TYPES, row.plan_type) },
  sorts: [
    { key: "review", label: "Review soonest", compare: byText<InterventionPlan>((row) => row.next_review_date ?? "9999") },
    { key: "-start", label: "Newest first", compare: reversed(byText<InterventionPlan>((row) => row.start_date)) },
    { key: "learner", label: "Learner (A–Z)", compare: byText<InterventionPlan>((row) => row.student_name) },
  ],
});

export function sessionStatus(row: Pick<InterventionAction, "status" | "scheduled_date">, now: Date = new Date()) {
  switch (row.status) {
    case "in_progress":
      return { label: "Under way", dot: "bg-sky-500" };
    case "completed":
      return { label: "Done", dot: "bg-emerald-500" };
    case "cancelled":
      return { label: "Cancelled", dot: "bg-gray-400" };
    default:
      if (new Date(row.scheduled_date) < now) return { label: "Overdue", dot: "bg-red-500" };
      return { label: row.status === "rescheduled" ? "Moved" : "Planned", dot: "bg-indigo-500" };
  }
}

// --- Patterns and classes --------------------------------------------------------------------

export function riskStatus(level: number) {
  if (level >= 5) return { label: "Critical", dot: "bg-red-600" };
  if (level >= 4) return { label: "High", dot: "bg-red-500" };
  if (level >= 3) return { label: "Medium", dot: "bg-amber-500" };
  return { label: level >= 2 ? "Low" : "Minimal", dot: "bg-emerald-500" };
}

export const patternConfig: RegisterConfig<BehaviorPattern> = {
  search: (row) => [row.student_name, row.student_id, row.student_class, row.category_name].join(" "),
  views: [
    { key: "support", label: "Support recommended", match: (row) => row.intervention_required },
    { key: "worsening", label: "Getting worse", match: (row) => row.trend_direction === "worsening" },
    { key: "all", label: "All" },
  ],
  date: { label: "Month", value: (row) => row.analysis_start_date },
  category: { label: "Behaviour", value: (row) => row.category_name },
  sorts: [
    { key: "-risk", label: "Highest risk", compare: (a, b) => b.risk_level - a.risk_level || b.incident_count - a.incident_count },
    { key: "-count", label: "Most incidents", compare: reversed(byNumber<BehaviorPattern>((row) => row.incident_count)) },
    { key: "learner", label: "Learner (A–Z)", compare: byText<BehaviorPattern>((row) => row.student_name) },
  ],
};

export const classConfig: RegisterConfig<ClassDisciplinarySummary> = {
  search: (row) => [row.class_name, row.most_common_category].join(" "),
  views: [
    { key: "incidents", label: "With incidents", match: (row) => row.total_incidents > 0 },
    { key: "all", label: "All classes" },
  ],
  sorts: [
    { key: "-total", label: "Most incidents", compare: reversed(byNumber<ClassDisciplinarySummary>((row) => row.total_incidents)) },
    { key: "-month", label: "Most this month", compare: reversed(byNumber<ClassDisciplinarySummary>((row) => row.incidents_this_month)) },
    { key: "name", label: "Class (A–Z)", compare: byText<ClassDisciplinarySummary>((row) => row.class_name) },
  ],
};

/** Share of a class's learners with at least one incident, as a whole percentage. */
export const shareAffected = (row: Pick<ClassDisciplinarySummary, "students_with_incidents" | "total_students">) =>
  row.total_students ? Math.round((row.students_with_incidents / row.total_students) * 100) : 0;

// --- Reports and definitions -----------------------------------------------------------------

export const reportConfig: RegisterConfig<DisciplinaryReport> = {
  search: (row) => [row.name, row.generated_by_name, row.student_filter_name, row.category_filter_name].join(" "),
  views: [{ key: "all", label: "All" }],
  date: { label: "Made on", value: (row) => row.generated_at },
  category: { label: "Report type", value: (row) => labelFrom(REPORT_TYPES, row.report_type) },
  sorts: [
    { key: "-when", label: "Newest first", compare: reversed(byText<DisciplinaryReport>((row) => row.generated_at)) },
    { key: "name", label: "Name (A–Z)", compare: byText<DisciplinaryReport>((row) => row.name) },
  ],
};

/** "Level 3" counts, highest first, from a report's level_N keys. */
export const severityRows = (breakdown: Record<string, number>) =>
  [5, 4, 3, 2, 1].map((level) => ({ label: severityLabel(level), count: breakdown[`level_${level}`] ?? 0 }));

export const definitionConfig = <T extends BehaviorCategory | DisciplinaryAction>(): RegisterConfig<T> => ({
  search: (row) => [row.name, row.description].join(" "),
  views: [
    { key: "active", label: "In use", match: (row) => row.is_active },
    { key: "retired", label: "Retired", match: (row) => !row.is_active },
    { key: "all", label: "All" },
  ],
  sorts: [
    { key: "severity", label: "Least serious first", compare: (a, b) => a.severity_level - b.severity_level || a.name.localeCompare(b.name) },
    { key: "name", label: "Name (A–Z)", compare: byText<T>((row) => row.name) },
    { key: "-used", label: "Most used", compare: reversed(byNumber<T>((row) => row.incident_count)) },
  ],
});
